import { supabase } from '../lib/supabase';

/**
 * Email Notification Service
 * Handles all email notifications through Supabase Edge Function
 */

const EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-email`;

/**
 * Send production status update email
 * @param {Object} params - Email parameters
 * @param {Array<string>} params.recipients - Array of email addresses
 * @param {string} params.playTitle - Title of the play
 * @param {number} params.completionPercentage - Completion percentage
 * @param {number} params.linesRecorded - Number of lines recorded
 * @param {number} params.totalLines - Total number of lines
 * @param {number} params.missingFiles - Number of missing files
 * @param {string} params.recipientName - Name of recipient
 */
export const sendProductionStatusUpdate = async ({
  recipients,
  playTitle,
  completionPercentage,
  linesRecorded,
  totalLines,
  missingFiles,
  recipientName = 'Team Member'
}) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        emailType: 'production_status_update',
        recipients,
        data: {
          playTitle,
          completionPercentage,
          linesRecorded,
          totalLines,
          missingFiles,
          recipientName
        }
      })
    });

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error?.error || 'Failed to send email');
    }

    return await response?.json();
  } catch (error) {
    console.error('Error sending production status update:', error);
    throw error;
  }
};

/**
 * Send assignment notification email
 * @param {Object} params - Email parameters
 * @param {Array<string>} params.recipients - Array of email addresses
 * @param {string} params.recipientName - Name of voice actor
 * @param {string} params.characterName - Character name
 * @param {string} params.playTitle - Play title
 * @param {number} params.lineCount - Number of lines
 * @param {number} params.estimatedDuration - Estimated duration in minutes
 * @param {string} params.dueDate - Due date
 * @param {string} params.priority - Priority level (high/medium/low)
 * @param {string} params.notes - Additional notes
 * @param {string} params.assignedBy - Name of person who assigned
 */
export const sendAssignmentNotification = async ({
  recipients,
  recipientName,
  characterName,
  playTitle,
  lineCount,
  estimatedDuration,
  dueDate,
  priority = 'medium',
  notes = '',
  assignedBy = 'Production Team'
}) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        emailType: 'assignment_notification',
        recipients,
        data: {
          recipientName,
          characterName,
          playTitle,
          lineCount,
          estimatedDuration,
          dueDate,
          priority,
          notes,
          assignedBy
        }
      })
    });

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error?.error || 'Failed to send email');
    }

    return await response?.json();
  } catch (error) {
    console.error('Error sending assignment notification:', error);
    throw error;
  }
};

/**
 * Send deadline alert email
 * @param {Object} params - Email parameters
 * @param {Array<string>} params.recipients - Array of email addresses
 * @param {string} params.recipientName - Name of recipient
 * @param {string} params.assignmentName - Assignment name
 * @param {string} params.characterName - Character name
 * @param {string} params.playTitle - Play title
 * @param {string} params.dueDate - Due date
 * @param {number} params.daysRemaining - Days remaining until deadline
 * @param {string} params.status - Current status
 * @param {number} params.lineCount - Number of lines
 */
export const sendDeadlineAlert = async ({
  recipients,
  recipientName,
  assignmentName,
  characterName,
  playTitle,
  dueDate,
  daysRemaining,
  status = 'pending',
  lineCount
}) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        emailType: 'deadline_alert',
        recipients,
        data: {
          recipientName,
          assignmentName,
          characterName,
          playTitle,
          dueDate,
          daysRemaining,
          status,
          lineCount
        }
      })
    });

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error?.error || 'Failed to send email');
    }

    return await response?.json();
  } catch (error) {
    console.error('Error sending deadline alert:', error);
    throw error;
  }
};

/**
 * Send recording session reminder email
 * @param {Object} params - Email parameters
 * @param {Array<string>} params.recipients - Array of email addresses
 * @param {string} params.recipientName - Name of voice actor
 * @param {string} params.sessionDate - Session date
 * @param {string} params.sessionTime - Session time
 * @param {string} params.location - Location
 * @param {Array<string>} params.characterNames - Array of character names
 * @param {number} params.estimatedDuration - Estimated duration in minutes
 * @param {string} params.notes - Additional notes
 */
export const sendRecordingSessionReminder = async ({
  recipients,
  recipientName,
  sessionDate,
  sessionTime,
  location,
  characterNames = [],
  estimatedDuration,
  notes = ''
}) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        emailType: 'recording_session_reminder',
        recipients,
        data: {
          recipientName,
          sessionDate,
          sessionTime,
          location,
          characterNames,
          estimatedDuration,
          notes
        }
      })
    });

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error?.error || 'Failed to send email');
    }

    return await response?.json();
  } catch (error) {
    console.error('Error sending recording session reminder:', error);
    throw error;
  }
};

/**
 * Send new follower notification email
 * @param {Object} params - Email parameters
 * @param {Array<string>} params.recipients - Array of email addresses
 * @param {string} params.recipientName - Name of voice actor
 * @param {string} params.followerName - Name of new follower
 * @param {number} params.followerCount - Total follower count
 * @param {string} params.profileUrl - URL to voice actor profile
 */
export const sendNewFollowerNotification = async ({
  recipients,
  recipientName,
  followerName,
  followerCount,
  profileUrl = '#'
}) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        emailType: 'new_follower_notification',
        recipients,
        data: {
          recipientName,
          followerName,
          followerCount,
          profileUrl
        }
      })
    });

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error?.error || 'Failed to send email');
    }

    return await response?.json();
  } catch (error) {
    console.error('Error sending new follower notification:', error);
    throw error;
  }
};

/**
 * Send follower summary email
 * @param {Object} params - Email parameters
 * @param {Array<string>} params.recipients - Array of email addresses
 * @param {string} params.recipientName - Name of voice actor
 * @param {string} params.summaryType - Type of summary (Daily/Weekly)
 * @param {number} params.newFollowers - Number of new followers
 * @param {number} params.totalFollowers - Total follower count
 * @param {Array<Object>} params.followersList - List of new followers with names and dates
 * @param {string} params.profileUrl - URL to voice actor profile
 */
export const sendFollowerSummary = async ({
  recipients,
  recipientName,
  summaryType = 'Weekly',
  newFollowers,
  totalFollowers,
  followersList = [],
  profileUrl = '#'
}) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    
    if (!session) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        emailType: 'follower_summary',
        recipients,
        data: {
          recipientName,
          summaryType,
          newFollowers,
          totalFollowers,
          followersList,
          profileUrl
        }
      })
    });

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error?.error || 'Failed to send email');
    }

    return await response?.json();
  } catch (error) {
    console.error('Error sending follower summary:', error);
    throw error;
  }
};

/**
 * Batch send production status updates to multiple admins
 * @param {Object} audioStats - Audio statistics object
 * @param {Array<Object>} adminUsers - Array of admin user objects with email and full_name
 */
export const batchSendProductionUpdates = async (audioStats, adminUsers) => {
  try {
    const emailPromises = adminUsers?.map(admin => 
      sendProductionStatusUpdate({
        recipients: [admin?.email],
        playTitle: 'All Productions',
        completionPercentage: audioStats?.completionPercentage || 0,
        linesRecorded: audioStats?.completedAudio || 0,
        totalLines: audioStats?.totalLines || 0,
        missingFiles: audioStats?.missingAudio || 0,
        recipientName: admin?.full_name || 'Admin'
      })
    );

    const results = await Promise.allSettled(emailPromises);
    
    const successful = results?.filter(r => r?.status === 'fulfilled')?.length;
    const failed = results?.filter(r => r?.status === 'rejected')?.length;

    return {
      success: true,
      sent: successful,
      failed,
      total: adminUsers?.length
    };
  } catch (error) {
    console.error('Error batch sending production updates:', error);
    throw error;
  }
};

/**
 * Check and send deadline alerts for upcoming assignments
 * @param {number} daysThreshold - Number of days to look ahead (default: 3)
 * @returns {Promise<Object>} Result with sent count
 */
export const checkAndSendDeadlineAlerts = async (daysThreshold = 3) => {
  try {
    // Import voiceActorService dynamically to avoid circular dependency
    const { voiceActorService } = await import('./voiceActorService');
    
    const upcomingDeadlines = await voiceActorService?.getUpcomingDeadlines(daysThreshold);

    if (!upcomingDeadlines || upcomingDeadlines?.length === 0) {
      return {
        success: true,
        sent: 0,
        message: 'No upcoming deadlines found'
      };
    }

    const emailPromises = upcomingDeadlines?.map(assignment => {
      if (!assignment?.voiceActor?.email) {
        return Promise.resolve({ skipped: true });
      }

      return sendDeadlineAlert({
        recipients: [assignment?.voiceActor?.email],
        recipientName: assignment?.voiceActor?.stageName || assignment?.voiceActor?.fullName || 'Voice Actor',
        assignmentName: `${assignment?.lineGroup?.characterName} Recording`,
        characterName: assignment?.lineGroup?.characterName,
        playTitle: assignment?.lineGroup?.playTitle,
        dueDate: new Date(assignment?.dueDate)?.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        daysRemaining: assignment?.daysRemaining,
        status: assignment?.status,
        lineCount: assignment?.lineGroup?.lineCount
      });
    });

    const results = await Promise.allSettled(emailPromises);
    
    const successful = results?.filter(r => r?.status === 'fulfilled' && !r?.value?.skipped)?.length;
    const failed = results?.filter(r => r?.status === 'rejected')?.length;

    return {
      success: true,
      sent: successful,
      failed,
      total: upcomingDeadlines?.length
    };
  } catch (error) {
    console.error('Error checking and sending deadline alerts:', error);
    throw error;
  }
};

export default {
  sendProductionStatusUpdate,
  sendAssignmentNotification,
  sendDeadlineAlert,
  sendRecordingSessionReminder,
  sendNewFollowerNotification,
  sendFollowerSummary,
  batchSendProductionUpdates,
  checkAndSendDeadlineAlerts
};